/**
 * Inline "add maintenance entry" form for the Maintenance blade.
 *
 * Replaces the old stacked-dialog flow with a panel that slides into the blade
 * itself: a date and an issue description, submitting via the new odl-app
 * maintenance endpoint. Collapses back to the list on save or cancel.
 *
 * Contains:
 * - MaintenanceAddForm — controlled inline form (default export)
 */
import { Button, Card, Stack, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createMaintenanceLog } from '@sophocles/api/odlApp';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import { parseApiError } from '@sophocles/utils/apiErrors';
import { formatDateForSubmission } from '@sophocles/utils/formatUtils';

// The maintenance-log payload's own field names; anything the backend
// reports outside this list downgrades to the form-wide error banner.
// `date_reported` is excluded — the DatePicker below has no error/helperText
// binding, so attributing to it would drop the message unread.
const MAINTENANCE_FIELDS = ['issue'];

// Filled multiline centers short text vertically in a tall slot (root
// align-items:center) and pads the root rather than the textarea — anchor
// content to the top instead.
const multilineFilledSx = {
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-inputMultiline': {
    alignSelf: 'flex-start',
    padding: '0 !important',
  },
};

/**
 * @param {object}        props
 * @param {number|string} props.siteId  - the site id for the new entry
 * @param {Function}      props.onDone  - called with the new entry object on success, or with no args on cancel
 */
function MaintenanceAddForm({ siteId, onDone }) {
  const { t } = useTranslation();

  const [dateReported, setDateReported] = useState(new Date());
  const [issue, setIssue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [issueError, setIssueError] = useState(null);
  const [nonFieldMessages, setNonFieldMessages] = useState([]);

  const canSubmit = issue.trim().length > 0 && !submitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setIssueError(null);
    setNonFieldMessages([]);
    createMaintenanceLog(siteId, {
      date_reported: formatDateForSubmission(dateReported),
      issue: issue.trim(),
    })
      .then(newEntry => {
        onDone?.(newEntry);
      })
      .catch(err => {
        const { fieldErrors, nonFieldMessages: messages } = parseApiError(err, {
          knownFields: MAINTENANCE_FIELDS,
          genericMessage: t('maintenance.saveFailed', 'Failed to save. Please try again.'),
        });
        setIssueError(fieldErrors.issue?.join(' ') ?? null);
        setNonFieldMessages(messages);
        setSubmitting(false);
      });
  };

  return (
    <Card sx={{ p: 2, bgcolor: 'var(--surface-secondary)' }}>
      <Stack spacing={2}>
        <DatePicker
          label={t('sites.dateOfTheIssue')}
          value={dateReported}
          maxDate={new Date()}
          onChange={setDateReported}
          slotProps={{ textField: { size: 'small', fullWidth: true } }}
        />
        <TextField
          required
          fullWidth
          multiline
          minRows={2}
          size="small"
          label={t('sites.issueDescription')}
          value={issue}
          onChange={e => setIssue(e.target.value)}
          error={Boolean(issueError)}
          helperText={issueError ?? undefined}
          sx={multilineFilledSx}
        />
        <FormErrorAlert messages={nonFieldMessages} />
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => onDone?.()} disabled={submitting}>
            {t('actions.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
            {t('actions.submit')}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}

MaintenanceAddForm.propTypes = {
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onDone: PropTypes.func.isRequired,
};

export default MaintenanceAddForm;
